import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import React from 'react';
import { useAuth } from '@auth/oauth/AuthProvider';
import { setAuthorizationHeader } from '@auth/utils/headerUtils';
import type { AuthErrorRecoveryEvent } from '@auth/utils/types';

type RetriedConfig = AxiosRequestConfig & { __authRetry?: boolean };

/**
 * Per-request opt-out, mirroring the legacy `errorHandler.ts` contract. A
 * request that sets `config.meta.skipErrorHandlers.skipAll = true` (or
 * `[status] = true`) bypasses recovery, logout, and not-found dispatch
 * entirely — the original error is propagated to the caller unchanged.
 */
type SkipErrorHandlersMeta = {
  skipAll?: boolean;
  [status: number]: boolean | undefined;
};

type RequestConfigWithMeta = AxiosRequestConfig & {
  meta?: { skipErrorHandlers?: SkipErrorHandlersMeta };
};

export type UseOauthErrorHandlerOptions = {
  /**
   * Called when recovery cannot complete — i.e. the silent token refresh
   * itself failed, OR the retried request still returned 401, OR a 403 was
   * observed.  The consumer is expected to drive the user
   * to logout (e.g. by invoking the function returned from `useLogout()`).
   *
   * The original Axios error is forwarded so the consumer can decide
   * whether to log it, surface a toast, etc.
   *
   * @universal Required for all apps.
   * @per-app
   *  - account, members, ecomm:    () => logout(window.location.href)
   *  - studio-registration:        () => logout({})
   *  - genie:                      () => loginWithRedirect()  // re-enters Auth0, NOT a full logout
   */
  onAuthFailure: (error: AxiosError) => void;

  /**
   * Optional 404-style handler. Called when the response is a 404, OR when
   * the response payload signals a missing resource via the legacy Peloton
   * conventions:
   *
   *  - `errorCode === 110` ("Resource does not exist")
   *  - `errorCode === 150` with a non-"already a participant" message
   *  - `message === 'Resource does not exist.'`
   *
   * The "Content Preview is Not Available" payload (errorCode 110 +
   * specific message) is silently swallowed — content previews routinely
   * 404 as part of normal flow and should not trigger a navigation.
   *
   * Clients typically wire this to a router-level redirect to a
   * not-found page. If `onNotFound` is not provided, 404s/110s propagate
   * unchanged (matching the request-only `useAuthClient` contract).
   *
   * @per-app
   *  - account, members, ecomm, genie, studio-registration:
   *      () => dispatch(redirectToNotFound())
   *  - any future app without a /not-found route: omit
   */
  onNotFound?: (error: AxiosError) => void;

  /**
   * URL substrings that bypass the silent-refresh recovery path on a 401.
   * A 401 from any of these endpoints propagates to the caller unchanged
   * — refreshing the token would be pointless (e.g. logging in / out /
   * changing password are themselves the credential-mutating endpoints).
   *
   * Defaults to `['/login', '/logout', '/change_password']`.
   *
   * @universal Default covers public auth endpoints every client app hits.
   *            Override only if your app has additional unauthenticated
   *            endpoints sharing this Axios client.
   */
  skipRecoveryUrls?: readonly string[];

  /**
   * URL substrings that should NOT trigger `onAuthFailure` even when a
   * 401/403 lands on them. The original error still rejects to the caller
   * so they can render an inline error state, but the user is not driven
   * to logout. Mirrors the legacy `errorHandler.ts` carve-outs for status
   * endpoints that are allowed to 401 without forcing a session reset.
   *
   * Defaults to `['/login', '/logout', '/change_password', '/api/me',
   * '/subscription/stream', '/primary_subscription']`.
   *
   * @per-app
   *  - members, ecomm: needs every default entry.
   *  - account: defaults; primarily relies on /api/me.
   *  - genie, studio-registration: defaults are harmless (endpoints unused).
   *
   * If your app calls a new endpoint that legitimately 401s without meaning
   * the user is logged out, append to this list IN YOUR APP WIRING
   */
  skipAuthFailureUrls?: readonly string[];

  /**
   * URL substrings that should NOT trigger `onNotFound` even when a 404
   * or `errorCode 110` lands on them. The original error still rejects to
   * the caller. Mirrors the legacy `isApiUserError` carve-out where
   * `api/user` 404s are allowed to surface inline rather than navigate.
   *
   * Defaults to `['api/user']`.
   *
   * @per-app
   *  - all five consumer apps: default `['api/user']` is sufficient today.
   *  - add an app-specific endpoint here only if your app silently 404s
   *    another resource that should not navigate.
   */
  skipNotFoundUrls?: readonly string[];

  /**
   * Optional observability hook. Called at every recovery checkpoint so
   * client apps can pipe events into Datadog RUM. The
   * recovery primitive itself stays vendor-agnostic — see
   * `@auth/monitoring` for the standard adapter.
   *
   * @universal Optional. Standard adapter lives in @auth/monitoring.
   */
  onAuthErrorEvent?: (
    event: AuthErrorRecoveryEvent,
    ctx?: Record<string, unknown>,
  ) => void;
};

const DEFAULT_SKIP_RECOVERY_URLS: readonly string[] = [
  '/login',
  '/logout',
  '/change_password',
];

const DEFAULT_SKIP_AUTH_FAILURE_URLS: readonly string[] = [
  '/login',
  '/logout',
  '/change_password',
  '/api/me',
  '/subscription/stream',
  '/primary_subscription',
];

const DEFAULT_SKIP_NOT_FOUND_URLS: readonly string[] = ['api/user'];

const isSkippedUrl = (url: string | undefined, urls: readonly string[]): boolean => {
  if (!url) {
    return false;
  }
  return urls.some(needle => url.indexOf(needle) !== -1);
};

/**
 * True if this response payload signals a missing resource via Peloton's
 * legacy API error-code convention. Mirrors `errorHandler.ts`'s
 * `doesResourceNotExist` predicate.
 */
const isResourceNotFoundPayload = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const d = data as { errorCode?: number; message?: string };
  if (d.errorCode === 110) {
    return true;
  }
  if (
    d.errorCode === 150 &&
    d.message !== 'User is already a participant of this challenge'
  ) {
    return true;
  }
  return d.message === 'Resource does not exist.';
};

/**
 * "Content Preview is Not Available" — content-preview endpoints 404
 * routinely as part of normal flow and must NOT navigate to not-found.
 */
const isContentPreviewPayload = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const d = data as { errorCode?: number; message?: string };
  return d.errorCode === 110 && d.message === 'Content Preview is Not Available';
};

const readSkipErrorHandlers = (
  config: RequestConfigWithMeta | undefined,
): SkipErrorHandlersMeta | undefined => config?.meta?.skipErrorHandlers;

const isOptedOut = (
  meta: SkipErrorHandlersMeta | undefined,
  status: number | undefined,
): boolean => {
  if (!meta) {
    return false;
  }
  if (meta.skipAll) {
    return true;
  }
  if (status != null && meta[status]) {
    return true;
  }
  return false;
};

/**
 * Registers an Axios **response** interceptor that provides full parity
 * with the legacy `useOauthErrorHandler` + `errorHandler` stack:
 *
 *  - **401** → silent-refresh + retry once; on failure → `onAuthFailure`.
 *  - **403** → `onAuthFailure` directly (no refresh path).
 *  - **404** / `errorCode 110` / `'Resource does not exist.'` →
 *    `onNotFound`, with a "Content Preview is Not Available" carve-out.
 *  - **`config.meta.skipErrorHandlers.skipAll | [status]`** → bypass all
 *    handlers; original error propagates.
 *  - **`skipAuthFailureUrls`** (login, logout, /api/me, /subscription/stream,
 *    /primary_subscription) — 401/403 on these endpoints does NOT fire
 *    `onAuthFailure`; the error rejects inline.
 *  - **`skipNotFoundUrls`** (api/user) — 404/110 on these endpoints does
 *    NOT fire `onNotFound`.
 *
 * App-specific behaviour MUST be expressed by passing options to
 * this hook from the app, never by adding a branch inside it. If you find
 * yourself wanting to write `if (app === 'members')`, add a new option instead.
 *
 * @example
 *   const client = useAuthClient({ baseURL });
 *   const logout = useLogout({ ... });
 *   const router = useRouter();
 *   useOauthErrorHandler(client, {
 *     onAuthFailure: () => logout(window.location.href),
 *     onNotFound: () => router.replace('/not-found'),
 *     onAuthErrorEvent: (ev, ctx) => trackAuthEvent(`auth.401_recovery.${ev}`, ctx),
 *   });
 */
export const useOauthErrorHandler = (
  client: AxiosInstance | undefined,
  {
    onAuthFailure,
    onNotFound,
    skipRecoveryUrls = DEFAULT_SKIP_RECOVERY_URLS,
    skipAuthFailureUrls = DEFAULT_SKIP_AUTH_FAILURE_URLS,
    skipNotFoundUrls = DEFAULT_SKIP_NOT_FOUND_URLS,
    onAuthErrorEvent,
  }: UseOauthErrorHandlerOptions,
): void => {
  const { refreshAccessToken } = useAuth();

  const onAuthFailureRef = React.useRef(onAuthFailure);
  onAuthFailureRef.current = onAuthFailure;
  const onNotFoundRef = React.useRef(onNotFound);
  onNotFoundRef.current = onNotFound;
  const onAuthErrorEventRef = React.useRef(onAuthErrorEvent);
  onAuthErrorEventRef.current = onAuthErrorEvent;

  // Single-flight cache for the in-flight refresh.
  const inFlightRefreshRef = React.useRef<Promise<string | null> | null>(null);

  const safeEmit = React.useCallback(
    (event: AuthErrorRecoveryEvent, ctx?: Record<string, unknown>) => {
      try {
        onAuthErrorEventRef.current?.(event, ctx);
      } catch {
        // no-op — observability must never break recovery
      }
    },
    [],
  );

  const safeFire = React.useCallback(
    (handler: ((error: AxiosError) => void) | undefined, error: AxiosError) => {
      if (!handler) {
        return;
      }
      try {
        handler(error);
      } catch {
        // no-op — consumer handler must not break the rejection path
      }
    },
    [],
  );

  const refreshToken = React.useCallback((): Promise<string | null> => {
    if (inFlightRefreshRef.current) {
      return inFlightRefreshRef.current;
    }
    const promise = refreshAccessToken().finally(() => {
      inFlightRefreshRef.current = null;
    });
    inFlightRefreshRef.current = promise;
    return promise;
  }, [refreshAccessToken]);

  const skipRecoveryUrlsSerialized = JSON.stringify(skipRecoveryUrls);
  const skipAuthFailureUrlsSerialized = JSON.stringify(skipAuthFailureUrls);
  const skipNotFoundUrlsSerialized = JSON.stringify(skipNotFoundUrls);

  React.useEffect(() => {
    if (!client) {
      return undefined;
    }

    const recoverySkipList: readonly string[] = JSON.parse(skipRecoveryUrlsSerialized);
    const authFailureSkipList: readonly string[] = JSON.parse(
      skipAuthFailureUrlsSerialized,
    );
    const notFoundSkipList: readonly string[] = JSON.parse(skipNotFoundUrlsSerialized);

    const handleResponse = (response: AxiosResponse) => response;

    const handleAuthFailure = async (
      error: AxiosError,
      status: 401 | 403,
      config: RetriedConfig,
    ): Promise<AxiosResponse> => {
      // 403 has no recoverable path — the backend has already decided this
      // session is unauthorized for this resource. Fire onAuthFailure
      // (unless the URL is in the auth-failure skip-list) and reject.
      if (status === 403) {
        if (isSkippedUrl(config.url, authFailureSkipList)) {
          safeEmit('skipped', { url: config.url, status: 403 });
          return Promise.reject(error);
        }
        safeEmit('forbidden', { url: config.url });
        safeFire(onAuthFailureRef.current, error);
        return Promise.reject(error);
      }

      // 401 path:
      // 1. URL in skip-list → no refresh, no logout.
      // 2. Already retried → no refresh, fall through to auth-failure-skip check.
      if (config.__authRetry || isSkippedUrl(config.url, recoverySkipList)) {
        safeEmit('skipped', { url: config.url, alreadyRetried: !!config.__authRetry });
        return Promise.reject(error);
      }

      safeEmit('attempted', { url: config.url });

      let freshToken: string | null;
      try {
        freshToken = await refreshToken();
      } catch (refreshError) {
        const err =
          refreshError instanceof Error ? refreshError : new Error(String(refreshError));
        safeEmit('failed', {
          url: config.url,
          reason: 'refresh_failed',
          name: err.name,
        });
        if (!isSkippedUrl(config.url, authFailureSkipList)) {
          safeFire(onAuthFailureRef.current, error);
        }
        return Promise.reject(error);
      }

      if (!freshToken) {
        safeEmit('failed', { url: config.url, reason: 'no_token' });
        if (!isSkippedUrl(config.url, authFailureSkipList)) {
          safeFire(onAuthFailureRef.current, error);
        }
        return Promise.reject(error);
      }

      config.__authRetry = true;
      setAuthorizationHeader(config, freshToken);

      try {
        const retried = await client.request(config);
        safeEmit('success', { url: config.url });
        return retried;
      } catch (retryError) {
        const retried = retryError as AxiosError;
        safeEmit('failed', {
          url: config.url,
          reason: 'retry_failed',
          status: retried?.response?.status,
        });
        if (!isSkippedUrl(config.url, authFailureSkipList)) {
          safeFire(onAuthFailureRef.current, retried);
        }
        return Promise.reject(retried);
      }
    };

    const handleNotFound = (
      error: AxiosError,
      config: RetriedConfig,
    ): Promise<AxiosResponse> => {
      const data = error?.response?.data;

      // Content Preview 404s are part of normal flow — swallow.
      if (isContentPreviewPayload(data)) {
        safeEmit('not_found_skipped', { url: config.url, reason: 'content_preview' });
        return Promise.reject(error);
      }

      if (isSkippedUrl(config.url, notFoundSkipList)) {
        safeEmit('not_found_skipped', { url: config.url, reason: 'skip_url' });
        return Promise.reject(error);
      }

      safeEmit('not_found', { url: config.url, status: error?.response?.status });
      safeFire(onNotFoundRef.current, error);
      return Promise.reject(error);
    };

    const handleError = async (error: AxiosError): Promise<AxiosResponse> => {
      const status = error?.response?.status;
      const config = error?.config as RetriedConfig | undefined;

      if (!config) {
        return Promise.reject(error);
      }

      // Per-request opt-out (legacy `meta.skipErrorHandlers` contract).
      const meta = readSkipErrorHandlers(config);
      if (isOptedOut(meta, status)) {
        safeEmit('opt_out', { url: config.url, status });
        return Promise.reject(error);
      }

      if (status === 401 || status === 403) {
        return handleAuthFailure(error, status, config);
      }

      // 404 OR resource-not-found payload (errorCode 110 etc.).
      const isNotFoundStatus = status === 404;
      const isNotFoundPayload = isResourceNotFoundPayload(error?.response?.data);
      if (isNotFoundStatus || isNotFoundPayload) {
        return handleNotFound(error, config);
      }

      return Promise.reject(error);
    };

    const interceptorId = client.interceptors.response.use(handleResponse, handleError);

    return () => {
      client.interceptors.response.eject(interceptorId);
    };
  }, [
    client,
    refreshToken,
    safeEmit,
    safeFire,
    skipRecoveryUrlsSerialized,
    skipAuthFailureUrlsSerialized,
    skipNotFoundUrlsSerialized,
  ]);
};

export default useOauthErrorHandler;
